// CTV-840: the "Get your business on TV this week" web banner, in the five
// sizes a station's web team and social accounts need. Built from the Island
// News set (Sep 2026); every station gets the same design in its own brand.
//
// What comes from the station's config, and where it lands:
//   • logo (inverted to white where the station says so), or the station's
//     name when it has no logo yet;
//   • the price pill, from the station's minimum budget;
//   • the "Seen on" line (tall sizes only), from the station's devices via the
//     CTV-810 helper, so it agrees with the landing page and the one-pager;
//   • every colour, derived from the station's hue by bannerPalette(). The
//     design's navy and sky blue were Island News's brand colours, and
//     hard-coding them would make every station's ad look like theirs.
//
// The node is rendered at its nominal CSS-pixel size (a 300x250 ad is a
// 300x250 box) and exported to PNG by html-to-image at DISPLAY_AD_EXPORT_SCALE.
// Plain CSS in a <style> block, like the print sheets: the export serialises
// computed styles, and the ad has to look identical on the page and in the
// file, which is easier to reason about without utility classes in the mix.
//
// The TV is the landing page's chrome asset (public/tv-chrome.webp) with the
// screen insets copied from MaterialSheet, so all three TVs are calibrated
// identically.

import React from 'react';
import { Check, Clock, MapPin, Play } from 'lucide-react';
import type { PublisherConfig } from '../../../types/selfService';
import { displayedName } from '../../../data/publisherConfigs';
import { resolveStationDevices } from '../streamingDeviceDisplay';
import { bannerPalette, type BannerLook } from './sheetColor';
import { DISPLAY_AD_SPECS, type DisplayAdSize } from './displayAdSizes';
import { useLogoKnockout } from './logoTone';

interface DisplayAdProps {
  config: PublisherConfig;
  size: DisplayAdSize;
  /** Dark is the design as drawn. Light keeps a station's logo as drawn. */
  look?: BannerLook;
  /**
   * A promotion the ad is run for (feedback 2026-09-04). The price pill
   * becomes the offer with the code, and the mobile strip, which has no
   * pill, puts the code on its button.
   */
  promo?: { code: string; bonusPercent: number } | null;
  /**
   * A saved still of the station's own footage (an anchor at the desk),
   * shown on the TV behind a dark scrim so "your ad here" still reads.
   * Absent: the plain screen. Decided 2026-09-03 over a lower-third caption,
   * which crowded the 300x250.
   */
  screenImageUrl?: string;
  /**
   * CTV-856: the rep's own referral code, printed on the email signature
   * graphic so it survives a forward or a print where the link is lost.
   * Null (a manager with no salesperson row) drops the line. Ignored by
   * the web sizes, which carry their attribution in the link beside them.
   */
  refCode?: string | null;
}

/** `items` dealt into rows of `per`; the last row takes the remainder. */
function chunk<T>(items: T[], per: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += Math.max(1, per)) rows.push(items.slice(i, i + per));
  return rows;
}

/** The TV set: chrome over a screen fill, or over a saved still. */
function tvSet(screenImageUrl?: string | null) {
  return (
    <div className="msad-tv">
      <img src={`${import.meta.env.BASE_URL}tv-chrome.webp`} alt="" aria-hidden />
      <div
        className={`msad-tv-screen${screenImageUrl ? ' msad-tv-screen--still' : ''}`}
        data-screen={screenImageUrl ? 'still' : 'plain'}
        style={screenImageUrl ? { backgroundImage: `url(${screenImageUrl})` } : undefined}
      >
        <div className="msad-tv-live">Live</div>
        <strong>YOUR AD HERE</strong>
        <em>a real commercial, made for you</em>
        <div className="msad-tv-bar">
          <Play fill="currentColor" strokeWidth={0} aria-hidden />
          <span className="msad-tv-track" />
          <span className="msad-tv-time">0:30</span>
        </div>
      </div>
    </div>
  );
}

function paletteVars(config: PublisherConfig, look: BannerLook): React.CSSProperties {
  const palette = bannerPalette(config, look);
  return {
    '--msad-bg-top': palette.bgTop,
    '--msad-bg-bottom': palette.bgBottom,
    '--msad-ink': palette.ink,
    '--msad-button': palette.button,
    '--msad-highlight': palette.highlight,
    '--msad-screen': palette.screen,
    '--msad-screen-deep': palette.screenDeep,
  } as React.CSSProperties;
}

/**
 * The TV on its own, at a given width, for the frame picker's preview
 * (feedback 2026-09-04): the still is what is being chosen, so the rest of
 * the ad is noise there. Same markup and palette as the ads.
 */
export const DisplayAdTv: React.FC<{
  config: PublisherConfig;
  look?: BannerLook;
  width: number;
  screenImageUrl?: string | null;
}> = ({ config, look = 'dark', width, screenImageUrl }) => (
  <>
    <style>{AD_CSS}</style>
    <div
      className={`msad msad--tv-only msad--${look}`}
      data-display-ad-tv
      style={{ width, fontSize: width * 0.06, ...paletteVars(config, look) }}
    >
      {tvSet(screenImageUrl)}
    </div>
  </>
);

const DisplayAd: React.FC<DisplayAdProps> = ({
  config,
  size,
  look = 'dark',
  promo = null,
  screenImageUrl,
  refCode = null,
}) => {
  const { width, height } = DISPLAY_AD_SPECS[size];
  const palette = bannerPalette(config, look);
  const name = displayedName(config);
  const minBudget = Number(config.minBudget ?? 0).toLocaleString('en-US');
  const deviceLabels = resolveStationDevices(config.devices).map((d) => d.label);
  // A logo URL that no longer resolves (an intake upload that was cleaned up)
  // would export as a blank corner. Fall back to the name, as with no logo.
  const [logoFailed, setLogoFailed] = React.useState(false);
  // A logo that would sink into the field (Channel 3000's navy mark on the
  // dark look, a white-on-clear mark on the light one) sits on a plate of the
  // opposite tone, as drawn, so its own colours survive. Feedback 2026-09-03:
  // a white knockout lost the brand. The station's logoInvert flag still
  // means "white on my dark header" and is honoured on the dark look.
  const sinks = useLogoKnockout(config.logoUrl, palette.bgTop);
  const invert = look === 'dark' && !!config.logoInvert;
  const plate = !invert && sinks;

  const brand = (
    <div className={`msad-brand${plate ? ' msad-brand--plated' : ''}`}>
      {config.logoUrl && !logoFailed ? (
        <span className={`msad-logo-box${plate ? ' msad-logo-plate' : ''}`}>
          <img
            className={`msad-logo${invert ? ' msad-logo--invert' : ''}`}
            src={config.logoUrl}
            alt={name}
            style={{ transform: `scale(${config.logoScale ?? 1})` }}
            onError={() => setLogoFailed(true)}
          />
        </span>
      ) : (
        <div className="msad-logo-text">{name}</div>
      )}
      <div className="msad-tagline">Streaming TV · Self-Serve</div>
    </div>
  );
  // The price stays even with a promo (feedback 2026-09-04): it is part of
  // the pitch. The promo goes on the button instead.
  const pill = <div className="msad-pill">from ${minBudget}</div>;
  // Feedback 2026-09-02: name the station, not "TV", so the reader sees what
  // they would be buying. Decided 2026-09-08: the display name, the one the
  // public sees everywhere else ("Channel 3000", not the WISC call sign),
  // falling back to the internal name. A long name steps the headline down
  // one size, and the strip fits its two lines whatever the length.
  const headlineName = name;
  // Feedback 2026-09-04: the mobile strip's headline is always exactly two
  // lines, "Get your business" over "on <station> this week", however long
  // the station's name. Each line refuses to wrap, and the text shrinks
  // just enough for the wider line to fit its column.
  const strip = size === '320x50';
  const headlineRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = headlineRef.current;
    if (!strip || !el) return;
    const fit = () => {
      // Start from the stylesheet's size (which includes the long-name step
      // down), then scale that by how far the wider line overshoots.
      el.style.fontSize = '';
      const width = el.clientWidth;
      const needed = el.scrollWidth;
      if (width > 0 && needed > width) {
        const base = parseFloat(getComputedStyle(el).fontSize);
        el.style.fontSize = `${Math.floor(base * (width / needed) * 10) / 10}px`;
      }
    };
    fit();
    // The column changes width once the logo loads; fit again then.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  });
  const headline = strip ? (
    <div className="msad-headline msad-headline--two-lines" ref={headlineRef}>
      <span>Get your business</span>{' '}
      <span>
        on <span className="msad-name">{headlineName}</span> <em>this week</em>
      </span>
    </div>
  ) : (
    <div className="msad-headline">
      Get your business on <span className="msad-name">{headlineName}</span> <em>this week</em>
    </div>
  );
  const longName = headlineName.length > 12;
  const tv = tvSet(screenImageUrl);
  const bullets = (
    <div className="msad-bullets">
      <span>
        <Clock aria-hidden /> Set up fast
      </span>
      <span>
        <Check aria-hidden /> No ad needed
      </span>
      <span>
        <MapPin aria-hidden /> Local reach
      </span>
    </div>
  );
  const seenOn = (
    <div className="msad-seen">
      <div className="msad-seen-label">Seen on</div>
      {/* Feedback 2026-09-04: the names are dealt into rows of a fixed count
          (three in the half page, one row in the square) rather than left
          to wrap, so six devices make two even lines and a line never
          starts or ends with a separator dot. */}
      <div className="msad-seen-list">
        {chunk(deviceLabels, size === '300x600' ? 3 : deviceLabels.length).map((row) => (
          <div key={row.join()}>{row.map((l) => l.replace(/ /g, '\u00a0')).join(' · ')}</div>
        ))}
      </div>
    </div>
  );
  // With a promo the button names the code and the bonus, in mixed case,
  // since the click applies the code but a reader who types the address
  // later needs to see it. Three lengths for three amounts of room: the
  // mobile strip fits the code alone, the leaderboard adds the bonus, and
  // the three sizes with a full-width or growing button spell it out.
  const promoLabel = (p: { code: string; bonusPercent: number }) => {
    if (size === '320x50') return `Code ${p.code} →`;
    if (size === '728x90' || size === '480x96') return `Code ${p.code} +${p.bonusPercent}% →`;
    return `Use Code ${p.code} +${p.bonusPercent}% Plays →`;
  };
  const cta = promo ? (
    <div className="msad-cta msad-cta--promo" data-promo={promo.code}>
      {promoLabel(promo)}
    </div>
  ) : (
    // CTV-856 (decided 2026-09-09): the signature sits under a person's name,
    // where "Start Now" reads pushy; a softer verb still says clickable.
    <div className="msad-cta">{size === '480x96' ? 'Learn More →' : 'Start Now →'}</div>
  );

  let body: React.ReactNode;
  switch (size) {
    case '320x50':
      body = (
        <>
          {brand}
          {headline}
          {cta}
        </>
      );
      break;
    case '728x90':
      body = (
        <>
          {brand}
          <div className="msad-rule" />
          {headline}
          {pill}
          {cta}
        </>
      );
      break;
    // CTV-856: the email signature. Logo, the headline over the rep's code,
    // and the button. No price pill: under an email it read as a receipt.
    case '480x96':
      body = (
        <>
          {brand}
          <div className="msad-rule" />
          <div className="msad-copy">
            {headline}
            {refCode && (
              <div className="msad-code" data-ref-code={refCode}>
                Click to apply code <strong>{refCode}</strong>
              </div>
            )}
          </div>
          {cta}
        </>
      );
      break;
    case '300x250':
      body = (
        <>
          <div className="msad-top">
            {brand}
            {pill}
          </div>
          {headline}
          {tv}
          {bullets}
          {cta}
        </>
      );
      break;
    case '300x600':
      body = (
        <>
          <div className="msad-top">
            {brand}
            {pill}
          </div>
          {headline}
          {tv}
          {bullets}
          {seenOn}
          {cta}
        </>
      );
      break;
    case '1080x1080':
      body = (
        <>
          {pill}
          {brand}
          {headline}
          {tv}
          {bullets}
          {seenOn}
          {cta}
        </>
      );
      break;
  }

  return (
    <>
      <style>{AD_CSS}</style>
      <div
        className={`msad msad--${size} msad--${look}${longName ? ' msad--long-name' : ''}`}
        data-display-ad={size}
        data-look={look}
        style={{ width, height, ...paletteVars(config, look) }}
      >
        {body}
      </div>
    </>
  );
};

const AD_CSS = `
.msad {
  box-sizing: border-box; position: relative; overflow: hidden;
  display: flex; flex-direction: column; justify-content: space-between;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Inter, sans-serif;
  color: var(--msad-ink); line-height: 1.15;
  background: linear-gradient(170deg, var(--msad-bg-top) 0%, var(--msad-bg-bottom) 100%);
  -webkit-font-smoothing: antialiased;
  /* The tints that follow the ink: dividers, muted text, the price pill. */
  --msad-line: rgba(255,255,255,.22);
  --msad-muted: rgba(255,255,255,.85);
  --msad-pill-bg: rgba(255,255,255,.12);
  --msad-pill-border: rgba(255,255,255,.35);
}
.msad--light {
  --msad-line: rgba(15,23,42,.18);
  --msad-muted: rgba(15,23,42,.72);
  --msad-pill-bg: rgba(15,23,42,.05);
  --msad-pill-border: rgba(15,23,42,.35);
}
.msad * { box-sizing: border-box; }
.msad-top { display: flex; justify-content: space-between; align-items: flex-start; }
/* flex-shrink: 0 matters in the strip layouts: a square logo is otherwise the
   first thing squeezed to nothing when the headline and button want room. */
.msad-brand { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; flex-shrink: 0; }
/* Logos are boxed by BOTH a width and a height cap. A wide wordmark hits the
   width cap and lands at the design's logo height; a square or stacked mark
   (KWWL's "7" badge) would be a speck at that height, so it is allowed up to
   about two and a half times it. The width cap keeps the two cases apart. */
.msad-logo { display: block; width: auto; height: auto; flex-shrink: 0; object-fit: contain; transform-origin: left center; }
.msad-logo--invert { filter: brightness(0) invert(1); }
/* The plate: a rounded tile of the opposite tone behind a logo that would
   otherwise sink into the field. Sized in em so one rule fits every banner. */
.msad-logo-box { display: inline-flex; align-items: center; }
.msad-logo-plate { padding: .3em .5em; border-radius: .4em; background: var(--msad-plate); }
/* Feedback 2026-09-04: a logo sits flush left over its tagline, but a plate
   flush left reads as misaligned (its edge, not the mark's, meets the text),
   so a plated logo is centred over the tagline instead, as in the square.
   Tried and rejected the same day: stretching the plate to the tagline's
   width, and hanging the plate into the margin so the mark aligns. */
.msad-brand--plated { align-items: center; }
.msad { --msad-plate: rgba(255,255,255,.94); }
.msad--light { --msad-plate: rgba(15,23,42,.9); }
.msad-logo-text { font-weight: 800; letter-spacing: -.02em; white-space: nowrap; }
.msad-tagline {
  font-weight: 700; letter-spacing: .14em; text-transform: uppercase;
  color: var(--msad-highlight); white-space: nowrap;
  /* Letter-spacing trails the last glyph, so the box runs one gap past the
     ink; trim it so anything centred on the tagline centres on its ink
     (feedback 2026-09-08: the plate sat a pixel right of centre). */
  margin-right: -.14em;
}
.msad-pill {
  flex-shrink: 0; white-space: nowrap; border-radius: 999px; font-weight: 700;
  background: var(--msad-pill-bg); border: 1px solid var(--msad-pill-border);
  /* Capitals and figures sit above the line box's centre (descender room is
     below the baseline), so the top gets a little more padding than the
     bottom and the text reads centred. Sizes set the base values. */
  line-height: 1.15;
  padding: calc(var(--pill-py) + .12em) var(--pill-px) calc(var(--pill-py) - .12em);
}
.msad-headline { font-weight: 800; letter-spacing: -.02em; font-size: var(--msad-hl); }
/* "on KITV 4 Island News" is three times the length of "on TV"; one step down
   keeps the headline to the same number of lines in most sizes. */
.msad--long-name .msad-headline { font-size: calc(var(--msad-hl) * .84); }
/* The station's name never splits across a line break ("KITV 4 Island /
   News" read as two things), and neither does "this week". */
.msad-name { white-space: nowrap; }
/* "this week" never splits across a line break; it reads as one beat. */
.msad-headline em { font-style: normal; color: var(--msad-highlight); white-space: nowrap; }
/* The strip's two fixed lines (see the note by the markup). */
.msad-headline--two-lines > span { display: block; white-space: nowrap; }
.msad-cta {
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: var(--msad-button); border-radius: 999px; color: #fff;
  font-weight: 800; letter-spacing: .05em; text-transform: uppercase; white-space: nowrap;
  /* Optical centring (feedback 2026-09-04). Letter-spacing trails the last
     glyph, so the indent adds the same space before the first; capitals sit
     above the line box's centre, so a transparent top border (inside the
     fixed height) drops them by half its width. */
  box-sizing: border-box; border-top: .2em solid transparent; text-indent: .05em;
}
/* The promo button keeps "Code" in mixed case; the code itself is caps already. */
.msad-cta--promo { text-transform: none; letter-spacing: .03em; text-indent: .03em; }
.msad-rule { flex-shrink: 0; width: 1px; background: var(--msad-line); align-self: stretch; }

/* The TV: chrome above a screen fill, insets from MaterialSheet (see the note
   there on why they overshoot). Everything inside is in em so one font-size
   on .msad-tv scales the whole set. */
/* The picker's preview: the set alone on nothing, filling its box. */
.msad--tv-only { display: block; overflow: visible; background: none; }
.msad--tv-only .msad-tv { width: 100%; transform: none; }

/* The TV is centred with align-self, never margin: auto. The PNG exporter
   copies computed styles onto a clone, and Safari reports an auto margin as
   0px, which pushed the set to the left edge of the file (2026-09-08). */
/* The remote hangs off the right of the chrome, so the screen's centre sits
   at 45.85% of the image, not 50%. Nudge the whole set (chrome, screen fill,
   still) right by the difference so the screen is what looks centred. */
.msad-tv { position: relative; flex-shrink: 0; transform: translateX(4.15%); }
.msad-tv > img { display: block; width: 100%; height: auto; position: relative; z-index: 1; }
.msad-tv-screen {
  position: absolute; top: 0.30%; left: 1.55%; right: 9.85%; bottom: 15.95%; z-index: 0;
  /* Screen runs from the brand mid tone to a deep one in BOTH looks: the
     screen is a lit set, and on the light look a fade to the pale field
     left the subtitle and the scrubber sitting on nothing. */
  background: linear-gradient(160deg, var(--msad-screen) 0%, var(--msad-screen-deep) 140%);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; color: #fff; gap: .25em;
}
/* A saved still fills the screen behind a dark scrim, so the label reads on
   any footage and the set still looks switched on. */
.msad-tv-screen--still { background-size: cover; background-position: center; isolation: isolate; text-shadow: 0 1px 4px rgba(0,0,0,.6); }
.msad-tv-screen--still::after {
  content: ''; position: absolute; inset: 0; z-index: -1;
  background: linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.6) 100%);
}
.msad-tv-screen strong { font-size: 1.6em; font-weight: 800; letter-spacing: -.02em; }
.msad-tv-screen em { font-style: normal; font-size: .82em; opacity: .9; }
.msad-tv-live {
  position: absolute; top: 11%; left: 7%; display: inline-flex; align-items: center; gap: .35em;
  background: rgba(255,255,255,.18); padding: .25em .6em; border-radius: 999px;
  font-size: .55em; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
  /* Hug the glyphs so the word and the dot centre together at tiny sizes. */
  line-height: 1;
}
/* flex: none keeps the dot round (a shrinkable flex item goes oval), and the
   baseline alignment centres it on the capitals rather than on the line box. */
.msad-tv-live { align-items: baseline; }
.msad-tv-live::before { content: ''; flex: none; width: .6em; height: .6em; border-radius: 50%; background: #ef4444; }
.msad-tv-bar {
  position: absolute; left: 7%; right: 7%; bottom: 9%;
  display: flex; align-items: center; gap: .5em;
}
.msad-tv-bar svg { width: .7em; height: .7em; flex-shrink: 0; }
.msad-tv-track { position: relative; flex: 1; height: .14em; border-radius: 2px; background: rgba(255,255,255,.38); }
.msad-tv-track::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 46%; background: #fff; border-radius: 2px; }
.msad-tv-track::after {
  content: ''; position: absolute; left: 46%; top: 50%; transform: translate(-50%, -50%);
  width: .5em; height: .5em; border-radius: 50%; background: #fff;
}
.msad-tv-time { font-size: .6em; font-weight: 700; flex-shrink: 0; }

/* Three equal columns, each label centred in its own, so the two dividers
   sit at the thirds rather than hugging the second and third labels. */
.msad-bullets { display: flex; align-items: stretch; font-weight: 700; }
.msad-bullets span {
  flex: 1 1 0; display: inline-flex; align-items: center; justify-content: center;
  gap: .45em; white-space: nowrap; min-width: 0;
}
.msad-bullets span + span { border-left: 1px solid var(--msad-line); }
.msad-bullets svg { color: var(--msad-highlight); flex-shrink: 0; }
.msad-seen-label { font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: var(--msad-highlight); }
.msad-seen-list { font-weight: 700; color: var(--msad-muted); }
.msad-seen-list > div { white-space: nowrap; }
.msad-seen-list > div + div { margin-top: .2em; }

/* --- 320 x 50: logo, two-line headline, button ----------------------------- */
.msad--320x50 { flex-direction: row; align-items: center; gap: 10px; padding: 0 12px; }
/* The tagline is hidden here, so the brand column has nothing but the logo to
   size itself by. An SVG logo with no intrinsic size then measures 0 wide and
   vanishes; a floor on the column gives the image a box to fit. */
.msad--320x50 .msad-brand { min-width: 70px; }
.msad--320x50 .msad-logo { max-height: 34px; max-width: 84px; }
.msad--320x50 .msad-logo-text { font-size: 12px; }
.msad--320x50 .msad-tagline { display: none; }
.msad--320x50 .msad-headline { flex: 1; min-width: 0; --msad-hl: 11.5px; line-height: 1.1; }
/* One fixed width for the strip's button, promo or not, so the headline's
   room beside it never changes; a promo code fits by giving up type size. */
.msad--320x50 .msad-cta { height: 22px; width: 84px; padding: 0 6px; font-size: 8.5px; }
.msad--320x50 .msad-cta--promo { font-size: 6.5px; letter-spacing: .03em; text-indent: .03em; }

/* --- 728 x 90: brand | headline, pill, button ------------------------------ */
.msad--728x90 { flex-direction: row; align-items: center; gap: 14px; padding: 0 20px; }
.msad--728x90 .msad-logo { max-height: 44px; max-width: 130px; }
.msad--728x90 .msad-logo-text { font-size: 15px; }
.msad--728x90 .msad-tagline { font-size: 6.5px; }
.msad--728x90 .msad-rule { height: 44px; align-self: center; }
/* The headline takes whatever width the logo, pill, and button leave. It must
   be allowed to wrap and to shrink (min-width: 0), or a station name the
   reader's font sets wider than ours pushes the button off the right edge. */
.msad--728x90 .msad-headline { flex: 1; min-width: 0; --msad-hl: 21px; line-height: 1.1; }
.msad--728x90 .msad-pill { font-size: 9px; --pill-py: 4px; --pill-px: 10px; }
.msad--728x90 .msad-cta { height: 36px; padding: 0 22px; font-size: 11px; }

/* --- 480 x 96: email signature (CTV-856): brand | headline + code, button -- */
/* The headline column is what is left after the logo and the button, about
   a third of the strip, so the type is set for two lines there. Sized up
   from a 400x80 draft by a fifth (2026-09-09: "slightly larger overall"). */
.msad--480x96 { flex-direction: row; align-items: center; gap: 12px; padding: 0 14px; }
.msad--480x96 .msad-logo { max-height: 41px; max-width: 100px; }
.msad--480x96 .msad-logo-text { font-size: 14px; }
.msad--480x96 .msad-tagline { font-size: 6px; }
.msad--480x96 .msad-rule { height: 43px; align-self: center; }
.msad--480x96 .msad-copy { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 7px; }
.msad--480x96 .msad-headline { --msad-hl: 14px; line-height: 1.1; }
/* A code is 4 to 16 characters. The line may wrap before the code, so a long
   one drops whole onto its own line rather than running under the button. */
.msad--480x96 .msad-code { font-size: 9px; font-weight: 600; letter-spacing: .04em; color: var(--msad-muted); line-height: 1.2; }
.msad--480x96 .msad-code strong { font-weight: 800; letter-spacing: .08em; color: var(--msad-highlight); white-space: nowrap; }
.msad--480x96 .msad-cta { height: 31px; padding: 0 14px; font-size: 10px; }
.msad--480x96 .msad-cta--promo { font-size: 8.5px; }

/* --- 300 x 250 ------------------------------------------------------------- */
.msad--300x250 { padding: 14px 15px 15px; }
.msad--300x250 .msad-logo { max-height: 34px; max-width: 96px; }
.msad--300x250 .msad-logo-text { font-size: 12px; }
.msad--300x250 .msad-tagline { font-size: 6.5px; }
.msad--300x250 .msad-pill { font-size: 8.5px; --pill-py: 3px; --pill-px: 9px; }
.msad--300x250 .msad-headline { --msad-hl: 17px; }
.msad--300x250 .msad-tv { width: 122px; align-self: center; font-size: 7.5px; }
/* The row runs edge to edge (past the padding) so the three cells have air. */
.msad--300x250 .msad-bullets { font-size: 8.5px; margin: 0 -15px; }
.msad--300x250 .msad-bullets svg { width: 9px; height: 9px; }
.msad--300x250 .msad-cta { height: 32px; font-size: 11px; }

/* --- 300 x 600 ------------------------------------------------------------- */
.msad--300x600 { padding: 18px; }
.msad--300x600 .msad-logo { max-height: 46px; max-width: 120px; }
.msad--300x600 .msad-logo-text { font-size: 16px; }
.msad--300x600 .msad-tagline { font-size: 7.5px; }
.msad--300x600 .msad-pill { font-size: 10px; --pill-py: 4px; --pill-px: 11px; }
/* 30px breaks as "Get your / business on TV / this week", the reference's three lines. */
.msad--300x600 .msad-headline { --msad-hl: 30px; }
.msad--300x600 .msad-tv { width: 260px; align-self: center; font-size: 15.5px; }
.msad--300x600 .msad-bullets { font-size: 9px; margin: 0 -18px; }
.msad--300x600 .msad-bullets svg { width: 10px; height: 10px; }
.msad--300x600 .msad-seen-label { font-size: 7px; }
.msad--300x600 .msad-seen-list { font-size: 10.5px; margin-top: 3px; }
.msad--300x600 .msad-cta { height: 40px; font-size: 12px; }

/* --- 1080 x 1080: centred for social -------------------------------------- */
.msad--1080x1080 { padding: 64px 72px 72px; align-items: center; text-align: center; }
.msad--1080x1080 .msad-pill { position: absolute; top: 60px; right: 72px; font-size: 26px; --pill-py: 12px; --pill-px: 32px; }
.msad--1080x1080 .msad-brand { align-items: center; gap: 12px; }
.msad--1080x1080 .msad-logo-plate { padding: 12px 22px; border-radius: 16px; }
.msad--1080x1080 .msad-logo { max-height: 150px; max-width: 380px; transform-origin: center; }
.msad--1080x1080 .msad-logo-text { font-size: 52px; }
.msad--1080x1080 .msad-tagline { font-size: 22px; }
.msad--1080x1080 .msad-headline { --msad-hl: 78px; max-width: 900px; }
.msad--1080x1080 .msad-tv { width: 500px; font-size: 30px; }
.msad--1080x1080 .msad-bullets { width: calc(100% + 144px); margin: 0 -72px; font-size: 26px; }
.msad--1080x1080 .msad-bullets svg { width: 30px; height: 30px; }
.msad--1080x1080 .msad-seen-label { font-size: 20px; }
.msad--1080x1080 .msad-seen-list { font-size: 28px; margin-top: 8px; }
.msad--1080x1080 .msad-cta { width: 480px; height: 104px; font-size: 38px; }
/* The promo label is longer than "Start Now"; the square has room, so the
   button grows to fit rather than clipping. */
.msad--1080x1080 .msad-cta--promo { width: auto; min-width: 480px; padding: 0 60px; }
`;

export default DisplayAd;
